import {
  AutomationRunStatus,
  type AutomationWorkflowRun,
  type DeliveryAttempt,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { getLabelForDateDifference } from '../../../../components/chat-history-panel/chat-history-panel.helpers';
import { getLocalCalendarDayDifference } from '../../../../helpers/date-time-helpers';
import { parseUtcToLocal } from '../shared/schedule.helpers';

interface RunPillStyle {
  color: string;
  background: string;
  label: string;
}

type AutomationRunDateGroup = {
  label: string;
  runs: AutomationWorkflowRun[];
};

/**
 * Maps an automation run status to its status-pill display style.
 *
 * @param {AutomationRunStatus} status - The run status returned by the API
 * @returns {RunPillStyle} Color, background, and human label for the pill
 */
function automationRunStatusPillStyle(status: AutomationRunStatus): RunPillStyle {
  switch (status) {
    case AutomationRunStatus.COMPLETED:
      return { color: '#146c43', background: '#d6f3e3', label: 'Succeeded' };
    case AutomationRunStatus.FAILED:
      return { color: '#a8160c', background: '#fddad7', label: 'Failed' };
    case AutomationRunStatus.PROCESSING:
      return { color: '#704a00', background: '#fcedc4', label: 'Running' };
    case AutomationRunStatus.PENDING:
      return { color: 'var(--ink-light)', background: 'var(--sky-lighter)', label: 'Queued' };
    case AutomationRunStatus.SKIPPED:
      return { color: 'var(--ink-light)', background: 'var(--sky-light)', label: 'Skipped' };
    default:
      return { color: 'var(--ink-base)', background: 'var(--sky-lighter)', label: status };
  }
}

/**
 * Groups automation runs into date sections (Today, Yesterday, Last 7 Days, …),
 * sorted newest-first, using the same date-bucketing helper as chat history.
 *
 * @param {AutomationWorkflowRun[]} runs - Runs to group
 * @returns {AutomationRunDateGroup[]} Date-grouped runs, newest-first
 */
function groupAutomationRunsByDate(runs: AutomationWorkflowRun[]): AutomationRunDateGroup[] {
  // Sort on the same key the bucket uses (started_at, else metadata_created_at)
  // so a just-queued PENDING run with no started_at stays at the top of its day
  // instead of sinking to the bottom on a 0 fallback.
  const sorted = [...runs].sort((a, b) => {
    const aTime = parseUtcToLocal(a.started_at ?? a.metadata_created_at)
      .toDate()
      .getTime();
    const bTime = parseUtcToLocal(b.started_at ?? b.metadata_created_at)
      .toDate()
      .getTime();
    return bTime - aTime;
  });
  const groups: AutomationRunDateGroup[] = [];

  for (const run of sorted) {
    const timestamp = run.started_at ?? run.metadata_created_at;
    const date = parseUtcToLocal(timestamp).toDate();
    const diffInDays = getLocalCalendarDayDifference(date);
    const label = getLabelForDateDifference(diffInDays, date);

    const existing = groups.find((group) => group.label === label);
    if (existing) {
      existing.runs.push(run);
    } else {
      groups.push({ label, runs: [run] });
    }
  }

  return groups;
}

/**
 * Returns the run duration in seconds between started_at and completed_at, or
 * null when the run hasn't finished or the timestamps are invalid.
 *
 * Uses raw `new Date(...)` rather than `parseUtcToLocal`: the result is a delta
 * of two timestamps, so any shared timezone-offset interpretation (UTC vs naive
 * local) cancels out and the duration is correct either way.
 *
 * @param {AutomationWorkflowRun} run - Run to measure
 * @returns {number | null} Duration in seconds, or null when unavailable
 */
function automationRunDurationSeconds(run: AutomationWorkflowRun): number | null {
  if (!run.started_at || !run.completed_at) return null;
  const start = new Date(run.started_at).getTime();
  const end = new Date(run.completed_at).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return (end - start) / 1000;
}

/**
 * Reports whether a run has expand-worthy details (error, output, or delivery
 * attempts). Gates the row's expand affordance so empty drawers never render.
 *
 * @param {AutomationWorkflowRun} run - Run to inspect
 * @returns {boolean} True when the run has error, output, or delivery details
 */
function automationRunHasDetails(run: AutomationWorkflowRun): boolean {
  return Boolean(run.error || run.output_text || run.delivery_attempts.length > 0);
}

/**
 * Reports whether every delivery attempt for a run targeted Slack — used to
 * swap the "View chat" affordance for an "Open in Slack" link.
 *
 * @param {AutomationWorkflowRun} run - Run to inspect
 * @returns {boolean} True when the run has attempts and all of them are Slack
 */
function isAutomationSlackOnlyRun(run: AutomationWorkflowRun): boolean {
  return run.delivery_attempts.length > 0 && run.delivery_attempts.every((attempt) => attempt.channel === 'slack');
}

/**
 * Builds a Slack deep link to a successfully-delivered message via Slack's
 * universal `app_redirect` entry point.
 *
 * @param {DeliveryAttempt} attempt - The delivery attempt to link
 * @returns {string | null} A Slack deep link, or null when the attempt isn't linkable
 */
function automationSlackDeepLink(attempt: DeliveryAttempt): string | null {
  if (attempt.channel !== 'slack' || attempt.status !== 'sent') return null;
  if (!attempt.team_id || !attempt.slack_channel_id || !attempt.thread_ts) return null;
  const params = new URLSearchParams({
    team: attempt.team_id,
    channel: attempt.slack_channel_id,
    message: attempt.thread_ts,
  });
  return `https://slack.com/app_redirect?${params.toString()}`;
}

/**
 * Returns the first linkable Slack delivery target for a run.
 *
 * @param {AutomationWorkflowRun} run - Run to inspect
 * @returns {string | null} A Slack deep link, or null when no sent Slack attempt exists
 */
function getAutomationPrimarySlackDeepLink(run: AutomationWorkflowRun): string | null {
  for (const attempt of run.delivery_attempts) {
    const link = automationSlackDeepLink(attempt);
    if (link) return link;
  }
  return null;
}

export {
  automationRunDurationSeconds,
  automationRunHasDetails,
  automationRunStatusPillStyle,
  getAutomationPrimarySlackDeepLink,
  groupAutomationRunsByDate,
  isAutomationSlackOnlyRun,
};
